import { useState, useCallback, useEffect } from 'react';
import { generateId, getStoredData, STORAGE_KEYS, createDebouncedCloudStorage, logger } from '@/utils';
import { mondayService } from '@/services/api';
import type { Lead, LeadList, Campaign, CampaignStep } from '@/types';

const debouncedCampaignsStorage = createDebouncedCloudStorage<Campaign[]>(STORAGE_KEYS.CAMPAIGNS, 'campaigns', 3000);

interface UseCampaignsDeps {
  leads: Lead[];
  setLeads: React.Dispatch<React.SetStateAction<{ data: Lead[]; loading: boolean; error: string | null }>>;
  leadLists: LeadList[];
  useMockData: boolean;
  initialData?: Campaign[];
}

export function useCampaigns({ leads, setLeads, leadLists, useMockData, initialData }: UseCampaignsDeps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>(() => {
    if (initialData && initialData.length > 0) return initialData;
    const stored = getStoredData<Campaign[]>(STORAGE_KEYS.CAMPAIGNS, []);
    return stored.length > 0 ? stored : [];
  });

  const [syncingCampaigns, setSyncingCampaigns] = useState<Set<string>>(new Set());

  // Persist campaigns to localStorage + DynamoDB
  useEffect(() => {
    debouncedCampaignsStorage.write(campaigns);
  }, [campaigns]);

  // Load from DynamoDB on mount (if localStorage is empty)
  useEffect(() => {
    if (campaigns.length === 0) {
      debouncedCampaignsStorage.loadFromCloud().then(cloudData => {
        if (cloudData && cloudData.length > 0) setCampaigns(cloudData);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCampaign = useCallback((campaignData: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    const newCampaign: Campaign = {
      ...campaignData,
      id: generateId('camp'),
      createdAt: today,
      updatedAt: today,
    };
    setCampaigns((prev) => [...prev, newCampaign]);
    return newCampaign.id;
  }, []);

  const updateCampaign = useCallback((campaignId: string, updates: Partial<Campaign>) => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    setCampaigns((prev) =>
      prev.map((campaign) =>
        campaign.id === campaignId
          ? { ...campaign, ...updates, updatedAt: today }
          : campaign
      )
    );
  }, []);

  const deleteCampaign = useCallback((campaignId: string) => {
    setCampaigns((prev) => prev.filter((campaign) => campaign.id !== campaignId));
  }, []);

  const addCampaignStep = useCallback(
    (campaignId: string, stepData: Omit<CampaignStep, 'id'>) => {
      const today = new Date().toISOString().split('T')[0] ?? '';
      const newStep: CampaignStep = {
        ...stepData,
        id: generateId('step'),
      };

      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === campaignId
            ? {
                ...campaign,
                steps: [...(campaign.steps ?? []), newStep],
                updatedAt: today,
              }
            : campaign
        )
      );

      return newStep.id;
    },
    []
  );

  const updateCampaignStep = useCallback(
    (campaignId: string, stepId: string, updates: Partial<CampaignStep>) => {
      const today = new Date().toISOString().split('T')[0] ?? '';

      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === campaignId
            ? {
                ...campaign,
                steps: (campaign.steps ?? []).map((step) =>
                  step.id === stepId ? { ...step, ...updates } : step
                ),
                updatedAt: today,
              }
            : campaign
        )
      );
    },
    []
  );

  const deleteCampaignStep = useCallback((campaignId: string, stepId: string) => {
    const today = new Date().toISOString().split('T')[0] ?? '';

    setCampaigns((prev) =>
      prev.map((campaign) => {
        if (campaign.id !== campaignId) return campaign;

        const filteredSteps = (campaign.steps ?? []).filter((step) => step.id !== stepId);
        const reorderedSteps = filteredSteps.map((step, index) => ({
          ...step,
          order: index + 1,
        }));

        return {
          ...campaign,
          steps: reorderedSteps,
          updatedAt: today,
        };
      })
    );
  }, []);

  const reorderCampaignSteps = useCallback(
    (campaignId: string, reorderedSteps: CampaignStep[]) => {
      const today = new Date().toISOString().split('T')[0] ?? '';

      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === campaignId
            ? {
                ...campaign,
                steps: reorderedSteps,
                updatedAt: today,
              }
            : campaign
        )
      );
    },
    []
  );

  const syncCampaignToMonday = useCallback(
    async (campaignId: string) => {
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) return { synced: 0, alreadySynced: 0 };

      setSyncingCampaigns((prev) => new Set(prev).add(campaignId));

      try {
        const campaignListIds = campaign.lists;
        const campaignLists = leadLists.filter((l) => campaignListIds.includes(l.id));

        const allLeadIds = new Set<string>();
        campaignLists.forEach((list) => {
          list.leads.forEach((leadId) => allLeadIds.add(leadId));
        });

        const leadsToSync = leads.filter(
          (lead) => allLeadIds.has(lead.id) && lead.apolloEnriched && !lead.syncedToMonday
        );

        const alreadySynced = leads.filter(
          (lead) => allLeadIds.has(lead.id) && lead.apolloEnriched && lead.syncedToMonday
        ).length;

        if (leadsToSync.length === 0) {
          logger.log(`[useCampaigns] No new leads to sync for campaign "${campaign.name}" (${alreadySynced} already synced)`);
          return { synced: 0, alreadySynced };
        }

        const today = new Date().toISOString();

        if (useMockData) {
          await new Promise((resolve) => setTimeout(resolve, 1500));

          setLeads((prev) => ({
            ...prev,
            data: prev.data.map((lead) =>
              leadsToSync.some((l) => l.id === lead.id)
                ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                : lead
            ),
          }));

          setCampaigns((prev) =>
            prev.map((c) =>
              c.id === campaignId ? { ...c, sentToMonday: true } : c
            )
          );

          logger.log(`[useCampaigns] Synced ${leadsToSync.length} leads from campaign "${campaign.name}" to Monday`);
          return { synced: leadsToSync.length, alreadySynced };
        } else {
          for (const lead of leadsToSync) {
            await mondayService.updateLead(lead.id, {
              apolloEnriched: true,
              apolloData: lead.apolloData,
            });
          }

          setLeads((prev) => ({
            ...prev,
            data: prev.data.map((lead) =>
              leadsToSync.some((l) => l.id === lead.id)
                ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                : lead
            ),
          }));

          setCampaigns((prev) =>
            prev.map((c) =>
              c.id === campaignId ? { ...c, sentToMonday: true } : c
            )
          );

          return { synced: leadsToSync.length, alreadySynced };
        }
      } finally {
        setSyncingCampaigns((prev) => {
          const next = new Set(prev);
          next.delete(campaignId);
          return next;
        });
      }
    },
    [campaigns, leadLists, leads, useMockData, setLeads]
  );

  // Handle the cross-domain part of assignCampaignToList (campaign side)
  const assignCampaignToListCampaignSide = useCallback((listId: string, campaignId: string | null) => {
    if (campaignId) {
      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === campaignId && !campaign.lists.includes(listId)
            ? { ...campaign, lists: [...campaign.lists, listId] }
            : campaign
        )
      );
    }
  }, []);

  const getCampaignsForLead = useCallback(
    (leadId: string) => {
      const listsWithLead = leadLists.filter((list) => list.leads.includes(leadId));
      const campaignIds = listsWithLead
        .map((list) => list.campaign)
        .filter((id): id is string => id !== null);
      return campaigns.filter((c) => campaignIds.includes(c.id) || c.lists.some((listId) =>
        leadLists.find((l) => l.id === listId)?.leads.includes(leadId)
      ));
    },
    [leadLists, campaigns]
  );

  return {
    campaigns,
    syncingCampaigns,

    // Actions
    createCampaign,
    updateCampaign,
    deleteCampaign,
    addCampaignStep,
    updateCampaignStep,
    deleteCampaignStep,
    reorderCampaignSteps,
    syncCampaignToMonday,
    assignCampaignToListCampaignSide,
    getCampaignsForLead,
  };
}

export type UseCampaignsReturn = ReturnType<typeof useCampaigns>;
